import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Grid
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { endpoints } from 'utils/axios';
import { useSnackbar } from 'components/snackbar';
import dayjs from 'dayjs';
import UpdateCompanyMasterForm from './UpdateCompanyMasterForm';
import ExcelJS from "exceljs";

const DATA_OPTIONS = [
  { name: "Industries", value: "IndustryMaster" },
  { name: "Skills", value: "SkillMaster" },
  { name: "Certifications", value: "CertificateMaster" },
  { name: "Institutes", value: "InstituteMaster" },
  { name: "Degrees", value: "DegreeMaster" },
  { name: "Roles", value: "RoleMaster" },
  { name: "Companies", value: "CompanyMaster" },
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel" // .xls
];

/**
 * Handles Master Data Update
 * 1. Download Current Master Data
 * 2. Upload Updated File for selected Master Data
 * 3. Upload Updated File for selected Master Data
 * 4. Search Company
 * 5. Edit Company
 * 6. Save Thought of the Day
 * @returns {JSX.Element}
 */
const MasterDataUpdate = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedData, setSelectedData] = useState("");
  const [file, setFile] = useState(null);
  const [isDownloading, setDownloading] = useState(false);

  // const [parsedData, setParsedData] = useState([]);
  const [uploadData, setUploadData] = useState("");
  const [dataIsUpdating, setDataIsUpdating] = useState(false);

  const [companyQuery, setCompanyQuery] = useState('');
  const [companyResults, setCompanyResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [updateCompany, setUpdateCompany] = useState(null);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [industryList, setIndustryList] = useState([]);

  const [thoughtText, setThoughtText] = useState('');

  useEffect(() => {
    console.log(selectedCompany);
    fetchCompanyData();
  }, [selectedCompany]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      /**
       * Fetches the companies that match the search query.
       * @returns {Promise<void>}
       * @listens setCompanyResults
       * @listens console.error
       */

      fetchCompanies();
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [companyQuery]);

  useEffect(() => {
    fetchIndustryList();
  }, []);

  const fetchCompanies = async () => {
    setIsCompanyLoading(true);
    // if (!companyQuery.trim()) {
    //   setCompanyResults([]);
    //   setIsCompanyLoading(false);
    //   return;
    // }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_END_POINT}${endpoints?.services?.company}?q=${encodeURIComponent(companyQuery)}`
      );
      const data = await response.json();
      setCompanyResults(data);
    } catch (err) {
      console.error("Company search error:", err);
      setCompanyResults([]);
    } finally {
      setIsCompanyLoading(false);
    }
  };

  const fetchIndustryList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.industry}`);
      const data = await response.json();
      setIndustryList(data || []);
    } catch (error) {
      console.error("Failed to fetch industry list", error);
    }
  };
  /**
     * Fetches the company data given the id and populates the form with the
     * retrieved data.
     * @returns {Promise<void>}
     * @listens setLoading
     * @listens setUpdateCompany
     * @listens enqueueSnackbar
     * @listens console.error
     */
  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const id = selectedCompany?._id;
      console.log(id);
      if (!id) return;
      const companyResponse = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.singleCompanyData}?id=${id}`);
      const companyData = await companyResponse.json();
      console.log(companyData);
      setUpdateCompany(companyData);
    } catch (err) {
      console.error("Update error:", err);
      enqueueSnackbar("Failed to update data.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };
  /**
   * Flattens an object with arrays as values into a new object where
    * each item in the array is a separate key - value pair.
   * @param { Object } obj The object with arrays as values.
   * @returns { Object } The flattened object.
   * @example
    * const obj = {
      *   a: [1, 2, 3],
      *   b: 'string',
      *   c: [4, 5, 6]
   * };
   *
   * const flattened = flattenArraysInObject(obj);
   * // flattened is equal to:
   * // {
   * //   a[0]: 1,
   * //   a[1]: 2,
   * //   a[2]: 3,
   * //   b: 'string',
   * //   c[0]: 4,
   * //   c[1]: 5,
   * //   c[2]: 6
   * // }
   */
  const flattenArraysInObject = (obj) => {
    const flattened = {};

    for (const key in obj) {
      const value = obj[key];

      if (Array.isArray(value)) {
        value.forEach((v, idx) => {
          flattened[`${key}[${idx}]`] = v;
        });
      } else {
        flattened[key] = value;
      }
    }

    return flattened;
  };

  /**
   * Downloads master data of the selected type as an Excel file.
   * Fetches data from the server, flattens any arrays within the data,
   * and then converts it into an Excel file that is automatically
   * saved to the user's device.
   * 
   * @async
   */
  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await fetch(
        `${process.env.REACT_APP_END_POINT}${endpoints?.services?.fetchMasterData}?type=${selectedData}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch data`);
      }

      const data = await response.json();

      if (!Array.isArray(data) || data.length === 0) {
        throw new Error("No data available to download.");
      }

      // Flatten arrays and add 'change_status'
      const flattenedData = data.map((item) => ({
        ...flattenArraysInObject(item),
        change_status: ""
      }));

      // Collect all unique headers from all objects
      const headersSet = new Set();
      flattenedData.forEach((item) => {
        Object.keys(item).forEach((key) => headersSet.add(key));
      });

      let headers = Array.from(headersSet);

      // Ensure 'change_status' appears at the end
      headers = headers.filter((h) => h !== 'change_status'); // remove if exists
      headers.push('change_status'); // add at end

      // Create workbook & worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(selectedData || "Sheet1");

      // Add header row
      worksheet.addRow(headers);

      // Add data rows
      flattenedData.forEach((rowObj) => {
        const row = headers.map((header) => rowObj[header] || ""); // fill missing keys with empty string
        worksheet.addRow(row);
      });

      // Find index of 'change_status' column
      const statusColIndex = headers.indexOf("change_status") + 1;

      // Add dropdown validation for all rows in Status column
      worksheet.dataValidations.add(
        `${worksheet.getColumn(statusColIndex).letter}2:${worksheet.getColumn(statusColIndex).letter}${flattenedData.length + 1}`,
        {
          type: "list",
          allowBlank: true,
          formulae: ['"add,delete,update"'],
          showInputMessage: true,
          showErrorMessage: true,
          errorTitle: "Invalid Option",
          error: "Please select a value from the list: Add, Delete, or Update",
        }
      );

      // Adjust column widths
      headers.forEach((header, i) => {
        worksheet.getColumn(i + 1).width = Math.max(header.length, 20);
      });

      // Generate Excel buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${selectedData || "data"}.xlsx`);

      enqueueSnackbar("File downloaded successfully!", { variant: "success" });
    } catch (err) {
      console.error("Download error:", err);
      enqueueSnackbar(err.message || "Failed to download file.", { variant: "error" });
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Handles the file upload event. It reads the uploaded file as
   * a binary string, parses it with XLSX, and stores the parsed data
   * in the `parsedData` state variable. The parsed data is a JSON
   * object with the same structure as the Excel file.
   * 
   * @param {Event} e - The file upload event.
   */

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    // ✅ check type
    if (!ALLOWED_TYPES.includes(uploadedFile.type)) {
      enqueueSnackbar("Only Excel files are allowed.", {
        variant: "error",
      });
      setFile(null);
      return;
    }

    // ✅ check size
    if (uploadedFile.size > MAX_FILE_SIZE) {
      enqueueSnackbar("File is too large. Please upload a file under 10MB.", {
        variant: "error",
      });
      setFile(null);
      return;
    }

    setFile(uploadedFile);

    // const reader = new FileReader();

    // /**
    //  * Event handler for the file reader's load event.
    //  * Parses the uploaded file as an Excel sheet and converts the data into JSON format.
    //  * Logs the parsed data to the console and updates the parsedData state.
    //  *
    //  * @param {Event} evt - The load event containing the file data.
    //  */

    // reader.onload = (evt) => {
    //   const bstr = evt.target.result;
    //   const workbook = XLSX.read(bstr, { type: "binary" });
    //   const sheet = workbook.Sheets[workbook.SheetNames[0]];
    //   const data = XLSX.utils.sheet_to_json(sheet);
    //   console.log("Parsed Upload Data:", data);
    //   setParsedData(data);
    // };
    // reader.readAsBinaryString(uploadedFile);
  };

  /**
   * Handles the form submission event. It checks if the uploadData state
   * and parsedData state are not empty, and if so, it sends a POST request
   * to the server with the type and data as JSON payload. If the request
   * is successful, it displays a success message to the user and clears
   * the file input. If the request fails, it logs the error to the console
   * and displays an error message to the user. If the error is a network
   * or server error, it displays a generic error message. If the error is
   * a payload too large error, it displays a specific error message.
   */
  const handleSubmitUpdate = async () => {
    if (!uploadData || !file) return;
    try {
      setDataIsUpdating(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", uploadData);

      const response = await fetch(
        `${process.env.REACT_APP_END_POINT}${endpoints?.services?.updateMasterData}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();
      enqueueSnackbar(result?.message || "Update complete.", { variant: result?.success ? "success" : "error" });
      setFile(null);
    } catch (err) {
      console.error("Update error:", err);
      enqueueSnackbar("Network or server error. Please try again later.", { variant: "error" });
    } finally {
      setDataIsUpdating(false);
    }
  };

  /**
   * Handles the update company form submission event. It checks if the
   * updateCompany state is not empty, and if so, it sends a POST request
   * to the server with the updated company data as JSON payload. If the
   * request is successful, it displays a success message to the user and
   * clears the updateCompany state. If the request fails, it logs the error
   * to the console and displays an error message to the user.
   */
  // const handleUpdateCompany = async () => {
  //   if (!updateCompany?._id) return;

  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_END_POINT}${endpoints?.services?.updateCompanyMaster}`,
  //       {
  //         method: "POST",
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //         body: JSON.stringify(updateCompany),
  //       }
  //     );

  //     const result = await response.json();
  //     setUpdateCompany(null);

  //     enqueueSnackbar(result?.message || "Company updated successfully.", { variant: result?.success ? "success" : "error" });
  //   } catch (err) {
  //     console.error("Company update error:", err);
  //     enqueueSnackbar("Failed to update company.", { variant: "error" });
  //   }
  // };

  /**
   * Handles the saving of the thought of the day.
   * @description
   * - Trims and checks if the thoughtText is not empty.
   * - If empty, displays a warning message.
   * - If not empty, sends a POST request to the server
   *   with the thoughtText as JSON payload.
   * - If the request is successful, displays a success
   *   message to the user and clears the thoughtText state.
   * - If the request fails, logs the error to the console
   *   and displays an error message to the user.
   */
  const handleSaveThought = async () => {

    if (!thoughtText.trim()) {
      enqueueSnackbar("The thought cannot be empty.", { variant: 'error' });
      return;
    }

    try {

      const endpointURL = `${process.env.REACT_APP_END_POINT}${endpoints.services.setThoughtOfTheDay}`;

      const response = await fetch(endpointURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },

        body: JSON.stringify({ thought: thoughtText }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        enqueueSnackbar(result?.message || 'Thought saved successfully!', { variant: result?.success ? 'success' : 'error' });

        setThoughtText('');
      } else {

        enqueueSnackbar(result?.message || 'Failed to save the thought.', { variant: 'error' });
      }

    } catch (err) {

      console.error("Error saving thought:", err);
      enqueueSnackbar('A network error occurred. Please try again.', { variant: 'error' });
    }
  };

  const handleClearUpdateCompany = () => setUpdateCompany(null);

  return (
    <Box sx={{ p: 4 }} mt={2}>
      <Typography variant="h4" gutterBottom>
        Update Master Data
      </Typography>

      {/* Section 1: Download Data */}
      <Card sx={{ mt: 3, p: 2, position: 'relative' }}>
        <CardContent>
          <Box>
            <Typography variant="h6">Download Current Master Data</Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ flex: 2 }}>
                <InputLabel>Select Data</InputLabel>
                <Select
                  value={selectedData}
                  label="Select Data"
                  onChange={(e) => setSelectedData(e.target.value)}
                >
                  {DATA_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                sx={{ height: "56px", flex: 1 }}
                disabled={!selectedData || isDownloading}
                onClick={handleDownload}
              >
                Download
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Section 2: Upload Updated File */}
      <Card sx={{ mt: 3, p: 2, position: 'relative' }}>
        <CardContent>
          <Box>
            <Typography variant="h6">
              Upload Updated File for "{uploadData || "..."}" (Max. 10MB)
            </Typography>
            <Stack direction="row" alignItems="center" spacing={3} sx={{ mt: 2 }}>
              <FormControl fullWidth sx={{ flex: 2 }}>
                <InputLabel>Select Data</InputLabel>
                <Select
                  value={uploadData}
                  label="Select Data"
                  onChange={(e) => setUploadData(e.target.value)}
                >
                  {DATA_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" component="label" disabled={!uploadData}>
                Upload File
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                />
              </Button>

              {file && <Typography>{file.name}</Typography>}

              {file && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmitUpdate}
                  disabled={dataIsUpdating}
                >
                  Submit Update to Database
                </Button>
              )}
            </Stack>

          </Box>
        </CardContent>
      </Card>

      {/* Section 3: Upload Updated File */}
      {/* <Card sx={{ mt: 3, p: 2, position: 'relative' }}>
        <CardContent>
          <Box>
            <Typography variant="h6" mb={2}>Search Company</Typography>

            <Autocomplete
              freeSolo
              fullWidth
              options={companyResults}
              getOptionLabel={(option) => option.name || ''}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') setCompanyQuery(value);
              }}
              onChange={(e, newValue) => {
                setSelectedCompany(newValue);
                setCompanyQuery(newValue?.name || '');
              }}
              value={selectedCompany}
              loading={isCompanyLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Enter company name"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isCompanyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {updateCompany && (
              <Card sx={{ mt: 3, p: 2, position: 'relative' }}>
                {loading ?
                  <CardContent>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 2,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </CardContent> :
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                      <Typography variant="h6">Company Id:</Typography>
                      <Typography>{updateCompany?._id || 'N/A'}</Typography>
                      {updateCompany?._id && (
                        <Tooltip title="Copy Company ID">
                          <IconButton
                            onClick={() => navigator.clipboard.writeText(updateCompany._id)}
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <UpdateCompanyMasterForm company={updateCompany} onClose={handleClearUpdateCompany} industryList={industryList} />
                  </CardContent>}
              </Card>
            )}

          </Box>
        </CardContent>
      </Card> */}

      {/* Section 4: Thought of the Day */}
      <Card sx={{ mt: 3, p: 2, position: 'relative' }}>
        <CardContent>
          <Box>
            <Typography variant="h6" gutterBottom>
              Thought of the Day
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>

              <TextField
                label="Enter your thought for the day"
                variant="outlined"
                value={thoughtText}
                onChange={(e) => setThoughtText(e.target.value)}
                sx={{ flexGrow: 1 }}
              />

              <Button
                variant="contained"
                onClick={handleSaveThought}
                disabled={!thoughtText.trim()}
                sx={{ flexShrink: 0 }}
              >
                Save
              </Button>
            </Stack>

          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MasterDataUpdate;
